import {
  MeasureReducer,
  ReportBuilderDataSourceId,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { FactTables } from '../../models/fact-tables';
import { toMeasureBuilderData } from '../../models/measure-properties';
import { ReportBuilderColumns } from '../../report-builder-columns';
import { ChartFilterDefinition } from '../charts/chart-filter-definition';
import { ReportBuilderCharts } from '../charts/report-builder-charts';
import { ReportBuilderCommonCharts } from '../charts/report-builder-common-charts';
import { ReportBuilderDataSourceInstance } from '../report-builder-data-source-instance';
import { ReportBuilderGroups } from '../report-builder-groups';
import { ReportBuilderScopeDataAccessors } from '../report-builder-scope-data-accessors';

export class AppointmentServiceCodesDataSource extends ReportBuilderDataSourceInstance {
  override readonly id = ReportBuilderDataSourceId.TreatmentStepServiceCodes;
  override readonly name = 'Service Codes by Appointment';
  override readonly description =
    'One result per service code within Principle. This includes information about the appointment, practice, and patient associated with each service code.';
  override readonly factTable =
    FactTables.appointmentServiceCodeEvent.latestEvent;
  override readonly rowItemName = 'Service Code';

  override readonly queryableTimestamps = [
    { property: this.factTable.appointment.date },
  ];

  scopes = [TemplateScope.Appointment, TemplateScope.Patient];
  scopeDataAccessor = ReportBuilderScopeDataAccessors.build([
    this.factTable.appointment,
    this.factTable.patient,
  ]);

  override readonly groupByOptions = [
    ReportBuilderGroups.toGroup('Common', [
      ReportBuilderGroups.toOption(this.factTable.practitioner),
      ReportBuilderGroups.toOption(this.factTable.practice.name),
      ReportBuilderGroups.toOption(this.factTable.brand.name),
      ReportBuilderGroups.toOption(this.factTable.code),
      ReportBuilderGroups.toOption(this.factTable.codeType),
      ReportBuilderGroups.toOption(this.factTable.treatmentName, [
        MeasureReducer.Count,
      ]),
      ReportBuilderGroups.toOption(this.factTable.treatmentCategory, [
        MeasureReducer.Count,
      ]),
      ReportBuilderGroups.toOption(this.factTable.chartedSurfaces, [
        MeasureReducer.Count,
      ]),
      ReportBuilderGroups.toOption(this.factTable.count),
    ]),
    ReportBuilderGroups.appointment(this.factTable.appointment),
    ReportBuilderGroups.invoice(this.factTable.invoice),
    ReportBuilderGroups.patient(this.factTable.patient),
  ];

  override readonly availableColumns = [
    ReportBuilderColumns.toColumnGroup('Service Code', [
      this.factTable.code,
      this.factTable.treatmentName,
      this.factTable.treatmentCategory,
      this.factTable.codeType,
      this.factTable.quantity,
      this.factTable.price,
      this.factTable.tax,
      this.factTable.chartedSurfaces,
      this.factTable.practitioner,
    ]),
    ReportBuilderColumns.toColumnGroup('Appointment', [
      ...ReportBuilderColumns.appointment(this.factTable.appointment),
      this.factTable.practice.name,
      this.factTable.treatmentPlan.name,
      this.factTable.treatmentPlanLink,
      this.factTable.appointmentLink,
    ]),
    ReportBuilderColumns.toColumnGroup('Invoice', [
      ...ReportBuilderColumns.invoice(this.factTable.invoice),
      this.factTable.invoiceLink,
    ]),
    ReportBuilderColumns.toColumnGroup('Patient', [
      ...ReportBuilderColumns.patient(this.factTable.patient),
      this.factTable.patientLink,
    ]),
  ];

  override readonly defaultColumns = ReportBuilderColumns.toReferences([
    this.factTable.appointment.date,
    this.factTable.patient.name,
    this.factTable.code,
    this.factTable.quantity,
    this.factTable.price,
    this.factTable.practitioner,
    this.factTable.appointment.practice,
    this.factTable.appointmentLink,
  ]);

  override readonly availableCharts = [
    ReportBuilderCharts.predefined(
      'overview.brand.price',
      this.factTable.brand.name,
      ChartFilterDefinition.numberSummary(
        'Total Price',
        this.factTable.brand.name,
        [toMeasureBuilderData(this.factTable.price)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.practice.price',
      this.factTable.practice.name,
      ChartFilterDefinition.pie(
        'Practice By Price',
        this.factTable.practice.name,
        [toMeasureBuilderData(this.factTable.price)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.practice.count',
      this.factTable.practice.name,
      ChartFilterDefinition.pie(
        'Practice By Count',
        this.factTable.practice.name,
        [toMeasureBuilderData(this.factTable.quantity)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.totalCost',
      this.factTable.brand.name,
      ChartFilterDefinition.numberSummary(
        'Service Code Total',
        this.factTable.brand.name,
        [toMeasureBuilderData(this.factTable.price)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.practitioner.price',
      this.factTable.practitioner,
      ChartFilterDefinition.row(
        `Practitioner By Price`,
        this.factTable.practitioner,
        [toMeasureBuilderData(this.factTable.price)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.practitioner.count',
      this.factTable.practitioner,
      ChartFilterDefinition.row(
        `Practitioner By Count`,
        this.factTable.practitioner,
        [toMeasureBuilderData(this.factTable.quantity)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.appointment.status',
      this.factTable.appointment.status,
      ChartFilterDefinition.pie(
        'Appointment Status',
        this.factTable.appointment.status,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.invoice.status',
      this.factTable.invoice.status,
      ChartFilterDefinition.pie(
        'Invoice Status',
        this.factTable.invoice.status,
        [toMeasureBuilderData(this.factTable.price)]
      )
    ),
    ReportBuilderCharts.predefined(
      'serviceCode.code.select',
      this.factTable.code,
      ChartFilterDefinition.selectMenu('Service Codes', this.factTable.code, [
        toMeasureBuilderData(this.factTable.quantity),
      ])
    ),
    ReportBuilderCharts.predefined(
      'serviceCode.code',
      this.factTable.code,
      ChartFilterDefinition.column(
        'Service Codes By Price',
        this.factTable.code,
        [toMeasureBuilderData(this.factTable.price, MeasureReducer.Sum)],
        { width: ReportBuilderCharts.gridSpan(3), rotateXAxisLabels: true }
      )
    ),
    ReportBuilderCharts.predefined(
      'serviceCode.type',
      this.factTable.codeType,
      ChartFilterDefinition.pie('Service Code Type', this.factTable.codeType, [
        toMeasureBuilderData(this.factTable.quantity, MeasureReducer.Count),
      ])
    ),
    ReportBuilderCharts.predefined(
      'serviceCode.treatment.name',
      this.factTable.treatmentName,
      ChartFilterDefinition.column(
        'Treatment',
        this.factTable.treatmentName,
        [toMeasureBuilderData(this.factTable.price, MeasureReducer.Sum)],
        { width: ReportBuilderCharts.gridSpan(3), rotateXAxisLabels: true }
      )
    ),
    ReportBuilderCharts.predefined(
      'serviceCode.treatmentCategory',
      this.factTable.treatmentCategory,
      ChartFilterDefinition.column(
        'Treatment Category',
        this.factTable.treatmentCategory,
        [toMeasureBuilderData(this.factTable.price)],
        { rotateXAxisLabels: true },
        { groupLimit: 15, otherGroupLabel: 'Other', colourOverride: true }
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.tags',
      this.factTable.appointment.tags,
      ChartFilterDefinition.row(
        'Tags',
        this.factTable.appointment.tags,
        [toMeasureBuilderData(this.factTable.price)],
        { groupLimit: 15, otherGroupLabel: 'Other' }
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.tags.select',
      this.factTable.appointment.tags,
      ChartFilterDefinition.selectMenu(
        'Tags',
        this.factTable.appointment.tags,
        [toMeasureBuilderData(this.factTable.price)]
      )
    ),
    ...ReportBuilderCommonCharts.patient(
      this.factTable.patient,
      this.factTable.price
    ),
  ];
}
